import { useState } from "react";
import { useLocation } from "wouter";
import { Sparkles } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { isVideoLivePath } from "@/lib/videoRoomPath";
import { isPublicLinkPage } from "@shared/publicLinks";
import { TeamCelebrationCard } from "./TeamCelebrationCard";
import { almatyCalendarDate } from "@shared/teamCelebrations";

/** A quiet daily card, independent of urgent notifications and live calls. */
export function TeamCelebrations() {
  const [location] = useLocation();
  if (isVideoLivePath(location) || isPublicLinkPage(location)) return null;
  return <TodayCelebrations />;
}

function TodayCelebrations() {
  const { user } = useAuth();
  const utils = trpc.useUtils();
  const [reopenedDate, setReopenedDate] = useState<string | null>(null);
  const [position, setPosition] = useState(0);
  const [hidden, setHidden] = useState<string[]>([]);
  const feed = trpc.teamCelebrations.today.useQuery(undefined, {
    enabled: Boolean(user),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
    retry: 1,
  });
  const dismiss = trpc.teamCelebrations.dismiss.useMutation({
    onSuccess: () => {
      void utils.teamCelebrations.today.invalidate();
    },
    // The card stays closed in this tab during a transient connection failure.
  });
  const items = feed.data?.date === almatyCalendarDate() ? feed.data.items : [];
  const reopened = reopenedDate === feed.data?.date;
  const visible = reopened
    ? items
    : items.filter(item => !item.dismissed && !hidden.includes(item.key));
  const index = visible.length ? position % visible.length : 0;
  const item = visible[index];
  if (!user || !items.length) return null;
  const close = () => {
    if (!item) return;
    setHidden(current => [...current, item.key]);
    setReopenedDate(null);
    setPosition(0);
    dismiss.mutate({ key: item.key });
  };
  return (
    <aside
      className="team-celebration-position"
      aria-label="Праздники команды"
      aria-live="polite"
    >
      {item ? (
        <TeamCelebrationCard
          item={item}
          own={item.userId === user.id}
          position={index}
          count={visible.length}
          onClose={close}
          onPrevious={() =>
            setPosition((index + visible.length - 1) % visible.length)
          }
          onNext={() => setPosition((index + 1) % visible.length)}
        />
      ) : (
        <button
          className="celebration-reopen"
          onClick={() => {
            setReopenedDate(feed.data?.date ?? null);
            setPosition(0);
          }}
        >
          <Sparkles size={16} /> Сегодня празднуем <span>{items.length}</span>
        </button>
      )}
    </aside>
  );
}
